"use client";

import dynamic from "next/dynamic";
import { useEffect, useMemo, useRef, useState } from "react";
import type { TechStackGroup } from "@/lib/content/types";
import { techOrbitBridge } from "@/components/three/techOrbitBridge";
import { isLiteGraphics } from "@/components/three/animation-utils";

const TechStackCanvas = dynamic(
  () =>
    import("@/components/three/TechStackCanvas").then(
      (mod) => mod.TechStackCanvas,
    ),
  { ssr: false },
);

type TechStackGraphProps = {
  groups: TechStackGroup[];
  hint?: string;
};

function applyTwoFingerOrbit(dx: number, dy: number, dDist: number) {
  const controls = techOrbitBridge.current;
  if (!controls) return;

  const camera = controls.object;
  const target = controls.target;
  const ox = camera.position.x - target.x;
  const oy = camera.position.y - target.y;
  const oz = camera.position.z - target.z;

  const radius = Math.hypot(ox, oy, oz);
  if (radius < 1e-6) return;

  let theta = Math.atan2(ox, oz);
  let phi = Math.acos(Math.min(1, Math.max(-1, oy / radius)));

  theta -= dx * 0.005;
  phi -= dy * 0.005;
  phi = Math.max(
    controls.minPolarAngle,
    Math.min(controls.maxPolarAngle, phi),
  );

  const sinPhi = Math.sin(phi);
  camera.position.set(
    target.x + radius * sinPhi * Math.sin(theta),
    target.y + radius * Math.cos(phi),
    target.z + radius * sinPhi * Math.cos(theta),
  );
  camera.lookAt(target);

  if (Math.abs(dDist) > 0.5) {
    const factor = Math.exp(-dDist * 0.008);
    if (factor < 1) controls.dollyIn(factor);
    else controls.dollyOut(1 / factor);
  } else {
    controls.update();
  }
}

/**
 * Sit above the WebGL canvas on touch devices so one-finger drags scroll the
 * page (OrbitControls forces touch-action:none). Two-finger gestures drive
 * orbit via the shared controls bridge; taps punch through for node picks.
 */
function MobileScrollShield() {
  const elRef = useRef<HTMLDivElement>(null);
  const startRef = useRef({ x: 0, y: 0, t: 0 });
  const pinchRef = useRef<{
    active: boolean;
    lastX: number;
    lastY: number;
    lastDist: number;
  }>({ active: false, lastX: 0, lastY: 0, lastDist: 0 });

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length >= 2) {
        event.preventDefault();
        const a = event.touches[0];
        const b = event.touches[1];
        if (!a || !b) return;
        pinchRef.current = {
          active: true,
          lastX: (a.clientX + b.clientX) / 2,
          lastY: (a.clientY + b.clientY) / 2,
          lastDist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        };
        return;
      }
      pinchRef.current.active = false;
      const touch = event.touches[0];
      if (!touch) return;
      startRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        t: Date.now(),
      };
    };

    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length < 2 || !pinchRef.current.active) return;
      event.preventDefault();
      const a = event.touches[0];
      const b = event.touches[1];
      if (!a || !b) return;

      const midX = (a.clientX + b.clientX) / 2;
      const midY = (a.clientY + b.clientY) / 2;
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      applyTwoFingerOrbit(
        midX - pinchRef.current.lastX,
        midY - pinchRef.current.lastY,
        dist - pinchRef.current.lastDist,
      );

      pinchRef.current.lastX = midX;
      pinchRef.current.lastY = midY;
      pinchRef.current.lastDist = dist;
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (event.touches.length >= 2) return;
      if (event.touches.length === 1) {
        pinchRef.current.active = false;
        return;
      }

      pinchRef.current.active = false;

      const touch = event.changedTouches[0];
      if (!touch) return;
      const dx = touch.clientX - startRef.current.x;
      const dy = touch.clientY - startRef.current.y;
      const dt = Date.now() - startRef.current.t;
      if (Math.hypot(dx, dy) >= 14 || dt >= 400) return;

      const canvas = el.parentElement?.querySelector("canvas");
      if (!canvas) return;
      el.style.pointerEvents = "none";
      const base: PointerEventInit = {
        bubbles: true,
        cancelable: true,
        clientX: touch.clientX,
        clientY: touch.clientY,
        pointerId: 1,
        pointerType: "touch",
        isPrimary: true,
      };
      canvas.dispatchEvent(
        new PointerEvent("pointerdown", { ...base, buttons: 1 }),
      );
      canvas.dispatchEvent(
        new PointerEvent("pointerup", { ...base, buttons: 0 }),
      );
      requestAnimationFrame(() => {
        el.style.pointerEvents = "auto";
      });
    };

    const onTouchCancel = () => {
      pinchRef.current.active = false;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchCancel, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchCancel);
    };
  }, []);

  return (
    <div
      ref={elRef}
      className="absolute inset-0 z-[6] md:hidden"
      style={{ touchAction: "pan-y" }}
      aria-hidden
    />
  );
}

export function TechStackGraph({ groups, hint }: TechStackGraphProps) {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [focusTick, setFocusTick] = useState(0);
  const [canvasMounted, setCanvasMounted] = useState(false);
  const [inView, setInView] = useState(false);
  const graphRef = useRef<HTMLDivElement>(null);
  const categories = useMemo(() => groups.map((g) => g.category), [groups]);

  useEffect(() => {
    // Never fetch the Three.js tech-graph chunk during PSI / lite mode.
    if (isLiteGraphics()) return;

    const el = graphRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (isLiteGraphics()) return;
        const visible = !!entry?.isIntersecting;
        setInView(visible);
        // Mount once on first intersect; stay mounted so we can pause/resume.
        if (visible) setCanvasMounted(true);
      },
      { rootMargin: "240px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const focusCategory = (category: string | null) => {
    setActiveCategory(category);
    setFocusTick((t) => t + 1);
  };

  return (
    <div className="w-full">
      <div className="flex flex-wrap justify-center gap-2 px-6 md:px-10">
        <button
          type="button"
          onClick={() => focusCategory(null)}
          className={`rounded-full px-3.5 py-1.5 text-xs tracking-wide transition-colors ring-1 ring-inset ${
            activeCategory == null
              ? "bg-white/[0.12] text-foreground ring-white/20"
              : "bg-white/[0.04] text-muted ring-white/[0.08] hover:text-foreground"
          }`}
        >
          All
        </button>
        {categories.map((category) => {
          const active = activeCategory === category;
          const isAi = category === "AI";
          return (
            <button
              type="button"
              key={category}
              onClick={() =>
                focusCategory(activeCategory === category ? null : category)
              }
              className={`rounded-full px-3.5 py-1.5 text-xs tracking-wide transition-colors ring-1 ring-inset ${
                active
                  ? "bg-white/[0.12] text-foreground ring-white/20"
                  : "bg-white/[0.04] text-muted ring-white/[0.08] hover:text-foreground"
              }`}
              style={
                active && isAi
                  ? { color: "#c4a574", boxShadow: "0 0 0 1px #c4a57444" }
                  : undefined
              }
            >
              {category}
            </button>
          );
        })}
      </div>

      <div
        ref={graphRef}
        className="relative mt-6 h-[min(108vw,720px)] min-h-[560px] w-full overflow-hidden md:h-[min(78vw,720px)] md:min-h-[480px]"
        data-tech-graph
        style={{
          touchAction: "pan-y",
          background:
            "radial-gradient(ellipse 75% 65% at 50% 50%, #121214 0%, #050506 55%, #000000 100%)",
        }}
      >
        <div className="absolute inset-0 z-0" data-tech-graph>
          {canvasMounted ? (
            <TechStackCanvas
              groups={groups}
              activeCategory={activeCategory}
              focusTick={focusTick}
              hoveredId={hoveredId}
              inView={inView}
              onHover={setHoveredId}
              onSelectCategory={(category) => focusCategory(category)}
              onResetView={() => focusCategory(null)}
            />
          ) : null}
        </div>

        {canvasMounted ? <MobileScrollShield /> : null}

        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-24 bg-gradient-to-b from-black via-black/70 to-transparent"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-28 bg-gradient-to-t from-black via-black/75 to-transparent"
          aria-hidden
        />

        {hint ? (
          <p className="pointer-events-none absolute inset-x-0 top-5 z-20 px-6 text-center text-[10px] uppercase tracking-[0.2em] text-muted/70 md:px-10">
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}
